import { IconAlertCircle, IconMail, IconSearch } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Link,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import { type FormEvent, useEffect, useState } from "react";
import { z } from "zod";
import {
	MarketingContainer,
	MarketingShell,
} from "@/components/misc/marketing-layout";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	DISPOSABLE_INBOX_PAGE_SIZE,
	type DisposableLookup,
	type DisposableMessage,
	type DisposableMessagesPage,
} from "@/data/custom/disposable";
import { cn } from "@/lib/utils";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";
import { seo } from "@/utils/seo";

/** Surfaces use theme tokens only so light and dark stay in sync with `main.css` variables. */
const inboxCardClass =
	"gap-0 border-border/60 bg-card py-0 shadow-sm ring-1 ring-border/30 transition-[box-shadow,border-color,background-color] dark:border-border dark:bg-muted/15 dark:shadow-[0_16px_48px_-20px_rgba(0,0,0,0.55)]";

const inboxShell = "marketing-neutral-headings";

export const inboxSearchSchema = z.object({
	page: z.coerce.number().int().min(1).catch(1),
	q: z.preprocess((val) => {
		if (val == null || val === "") return undefined;
		const s = String(val).trim().slice(0, 200);
		return s === "" ? undefined : s;
	}, z.string().optional()),
});

export const Route = createFileRoute("/_marketing/inbox/$localPart")({
	component: InboxPage,
	validateSearch: (search: Record<string, unknown>) => {
		const parsed = inboxSearchSchema.safeParse(search);
		if (!parsed.success) return { page: 1 };
		const { page, q } = parsed.data;
		return { page, ...(q ? { q } : {}) };
	},
	head: ({ params }) =>
		seo({
			title: `Inbox · ${params.localPart}`,
			description: `Temporary privacy-focused inbox for ${params.localPart}`,
		}),
});

export function decodeLocalPart(s: string) {
	try {
		return decodeURIComponent(s);
	} catch {
		return s;
	}
}

export function InboxPage() {
	const raw = Route.useParams().localPart;
	const localPart = decodeLocalPart(raw);
	const { page, q } = useSearch({ from: Route.id });
	const navigate = useNavigate();
	const [draftQ, setDraftQ] = useState(q ?? "");

	useEffect(() => {
		setDraftQ(q ?? "");
	}, [q]);

	const lookup = useQuery({
		queryKey: ["disposable-lookup", localPart],
		queryFn: async () => {
			const res = await requestWithHandling(() =>
				apiClient.api.disposableLookupList({ local_part: localPart }),
			);
			if (res.code !== "SUCCESS" || res.data === undefined) {
				throw new TypedClientApiError(
					res.message || res.code || "Request failed",
					undefined,
					null,
					res.code,
				);
			}
			return res.data as unknown as DisposableLookup;
		},
		refetchInterval: 15_000,
	});

	const active = lookup.data?.status === "active";

	const messages = useQuery({
		queryKey: ["disposable-messages", localPart, page, q ?? ""],
		queryFn: async () => {
			const search = q?.trim().slice(0, 200) ?? "";
			const res = await requestWithHandling(() =>
				apiClient.api.disposableMessagesList({
					local_part: localPart,
					page,
					limit: DISPOSABLE_INBOX_PAGE_SIZE,
					...(search !== "" ? { q: search } : {}),
				}),
			);
			if (res.code !== "SUCCESS" || res.data === undefined) {
				throw new TypedClientApiError(
					res.message || res.code || "Request failed",
					undefined,
					null,
					res.code,
				);
			}
			return res.data as unknown as DisposableMessagesPage;
		},
		enabled: active,
		refetchInterval: active ? 8_000 : false,
	});

	useEffect(() => {
		const d = messages.data;
		if (!d || d.status !== "active" || d.total_pages <= 0) return;
		if (page > d.total_pages) {
			void navigate({
				to: "/inbox/$localPart",
				params: { localPart: raw },
				search: (prev) => ({ ...prev, page: d.total_pages }),
				replace: true,
			});
		}
	}, [messages.data, page, navigate, raw]);

	if (lookup.isLoading) {
		return (
			<MarketingShell>
				<MarketingContainer
					className={cn("py-24 text-center text-muted-foreground", inboxShell)}
				>
					Loading inbox…
				</MarketingContainer>
			</MarketingShell>
		);
	}

	if (lookup.isError || !lookup.data) {
		return (
			<MarketingShell>
				<MarketingContainer className={cn("py-24 text-center", inboxShell)}>
					<p className="text-destructive">Could not load this inbox.</p>
					<Link
						to="/"
						className={cn(
							buttonVariants({ variant: "outline" }),
							"mt-6 inline-flex",
						)}
					>
						Home
					</Link>
				</MarketingContainer>
			</MarketingShell>
		);
	}

	if (lookup.data.status === "not_found") {
		return (
			<MarketingShell>
				<MarketingContainer className={cn("max-w-lg py-24", inboxShell)}>
					<Card
						className={cn(
							inboxCardClass,
							"shadow-2xl border-0 bg-gradient-to-br from-white via-muted/60 to-muted dark:from-[#222232] dark:via-muted/40 dark:to-muted",
						)}
					>
						<CardHeader className="py-4">
							<div className="flex items-center gap-3">
								<div className="rounded-full bg-amber-100 p-3 dark:bg-amber-950">
									<IconAlertCircle className="size-7 text-amber-600 dark:text-amber-400" />
								</div>
								<span className="font-semibold text-lg text-amber-800 dark:text-amber-300">
									No inbox found
								</span>
							</div>
						</CardHeader>
						<CardContent className="space-y-7 pt-3 text-muted-foreground">
							<div className="space-y-4">
								<p className="text-base text-foreground/80 font-medium">
									We couldn't locate an active mailbox for this URL.
								</p>
								<div className="flex items-center gap-2">
									<span className="text-muted-foreground/70 text-sm">
										Address:
									</span>
									<span className="rounded-md bg-muted px-2.5 py-1 font-mono text-[1rem] font-semibold tracking-tight text-foreground border border-border/70">
										{localPart}
									</span>
								</div>
							</div>
							<div className="space-y-3 bg-muted/80 rounded-lg p-3 border border-border/70 shadow-sm">
								<p className="font-semibold text-foreground mb-1 text-sm">
									What might have happened:
								</p>
								<ul className="list-disc pl-5 space-y-1.5 marker:text-amber-500 text-muted-foreground text-[0.97rem]">
									<li>
										The link was incomplete (missing characters at start or
										end).
									</li>
									<li>
										The inbox never existed here, or the link was typed/pasted
										incorrectly.
									</li>
									<li>
										The inbox expired/was deleted—only the unique URL you saved
										can reopen mail.
									</li>
								</ul>
							</div>
							<div className="text-sm text-foreground/70">
								If you have the original inbox link, please open it again.
								Otherwise, you can start fresh:
							</div>
							<div className="flex flex-row gap-3 py-4">
								<Link
									to="/"
									className={cn(
										buttonVariants({ variant: "default" }),
										"inline-flex text-base font-semibold px-5",
									)}
								>
									New inbox
								</Link>
								<Link
									to="/contact"
									className={cn(
										buttonVariants({ variant: "outline" }),
										"inline-flex text-base px-5",
									)}
								>
									Contact Support
								</Link>
							</div>
						</CardContent>
					</Card>
				</MarketingContainer>
			</MarketingShell>
		);
	}

	if (lookup.data.status === "destroyed") {
		return (
			<MarketingShell>
				<MarketingContainer className={cn("max-w-lg py-24", inboxShell)}>
					<Card className={inboxCardClass}>
						<CardHeader>
							<CardTitle className="flex items-center gap-2 text-lg">
								<IconMail className="size-5" />
								This inbox has expired
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4 text-muted-foreground">
							<p>
								Self-destruct time passed for{" "}
								<code className="rounded bg-muted px-1.5 py-0.5 text-foreground">
									{lookup.data.local_part}
								</code>
								. Messages are no longer available.
							</p>
							<Link to="/" className={cn(buttonVariants(), "inline-flex")}>
								Get a new address
							</Link>
						</CardContent>
					</Card>
				</MarketingContainer>
			</MarketingShell>
		);
	}

	const data = lookup.data;
	const list = messages.data?.messages ?? [];
	const total = messages.data?.total ?? 0;
	const totalPages = messages.data?.total_pages ?? 0;
	const limit = messages.data?.limit ?? DISPOSABLE_INBOX_PAGE_SIZE;
	const fromIdx = total === 0 ? 0 : (page - 1) * limit + 1;
	const toIdx = total === 0 ? 0 : Math.min(page * limit, total);

	const goPage = (next: number) => {
		void navigate({
			to: "/inbox/$localPart",
			params: { localPart: raw },
			search: (prev) => ({ ...prev, page: next }),
		});
	};

	const applySearch = (e: FormEvent) => {
		e.preventDefault();
		const t = draftQ.trim().slice(0, 200);
		void navigate({
			to: "/inbox/$localPart",
			params: { localPart: raw },
			search: { page: 1, ...(t ? { q: t } : {}) },
		});
	};

	const clearSearch = () => {
		setDraftQ("");
		void navigate({
			to: "/inbox/$localPart",
			params: { localPart: raw },
			search: { page: 1 },
		});
	};

	return (
		<MarketingShell>
			<MarketingContainer
				className={cn("max-w-3xl py-12 sm:py-16", inboxShell)}
			>
				<div className="mb-10 flex flex-col gap-6 rounded-2xl border border-border/70 bg-card p-6 shadow-sm ring-1 ring-border/25 sm:flex-row sm:items-start sm:justify-between sm:p-8 dark:border-border dark:bg-muted/10 dark:shadow-[0_10px_40px_-12px_rgba(0,0,0,0.5)]">
					<div className="min-w-0 flex-1 space-y-2">
						<h1 className="text-3xl font-semibold tracking-tight text-foreground">
							Inbox
						</h1>
						<p className="break-all font-mono text-sm leading-relaxed text-foreground">
							{data.address}
						</p>
						<p className="text-sm text-muted-foreground">
							Expires{" "}
							<time
								className="text-foreground tabular-nums"
								dateTime={data.expires_at}
							>
								{new Date(data.expires_at).toLocaleString()}
							</time>
						</p>
					</div>
					<Link
						to="/"
						className={cn(
							buttonVariants({ variant: "default", size: "sm" }),
							"inline-flex shrink-0 sm:mt-1",
						)}
					>
						New inbox
					</Link>
				</div>

				<form
					className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end"
					onSubmit={applySearch}
				>
					<div className="grid w-full min-w-0 flex-1 gap-2 sm:max-w-md">
						<Label htmlFor="inbox-search">Search subject or sender</Label>
						<Input
							id="inbox-search"
							value={draftQ}
							onChange={(e) => setDraftQ(e.target.value)}
							placeholder="e.g. invoice or noreply@"
							autoComplete="off"
							spellCheck={false}
							maxLength={200}
						/>
					</div>
					<div className="flex shrink-0 flex-wrap gap-2">
						<Button type="submit" size="sm" className="gap-1.5">
							<IconSearch className="size-4" aria-hidden />
							Search
						</Button>
						{q ? (
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={clearSearch}
							>
								Clear
							</Button>
						) : null}
					</div>
				</form>

				<div className="space-y-6">
					{messages.isLoading && (
						<p className="text-sm text-muted-foreground">Loading messages…</p>
					)}
					{!messages.isLoading && list.length === 0 && (
						<Card className={inboxCardClass}>
							<CardContent className="py-12 text-center text-muted-foreground">
								{q
									? "No messages match this search. Try different words or clear the filter."
									: "No mail yet. Waiting for incoming messages…"}
							</CardContent>
						</Card>
					)}
					{list.map((m) => (
						<MessageCard key={m.id} message={m} />
					))}
					{total > 0 ? (
						<div className="flex flex-col gap-4 border-t border-border/60 pt-6 sm:flex-row sm:items-center sm:justify-between">
							<p className="text-sm text-muted-foreground">
								Showing{" "}
								<span className="font-medium text-foreground">
									{fromIdx}–{toIdx}
								</span>{" "}
								of <span className="font-medium text-foreground">{total}</span>
								{totalPages > 1 ? (
									<>
										{" "}
										· page{" "}
										<span className="tabular-nums text-foreground">{page}</span>{" "}
										of{" "}
										<span className="tabular-nums text-foreground">
											{totalPages}
										</span>
									</>
								) : null}
							</p>
							{totalPages > 1 ? (
								<div className="flex flex-wrap gap-2">
									<Button
										type="button"
										variant="outline"
										size="sm"
										disabled={page <= 1 || messages.isFetching}
										onClick={() => goPage(page - 1)}
									>
										Previous
									</Button>
									<Button
										type="button"
										variant="outline"
										size="sm"
										disabled={page >= totalPages || messages.isFetching}
										onClick={() => goPage(page + 1)}
									>
										Next
									</Button>
								</div>
							) : null}
						</div>
					) : null}
				</div>
			</MarketingContainer>
		</MarketingShell>
	);
}

function MessageCard({ message }: { message: DisposableMessage }) {
	return (
		<Card className={inboxCardClass}>
			<CardHeader className="space-y-2 border-b border-border/50 bg-muted/25 px-5 pb-4 pt-5 dark:bg-muted/20">
				<CardTitle className="text-lg font-semibold leading-snug text-foreground not-italic">
					{message.subject || "(no subject)"}
				</CardTitle>
				<p className="text-xs leading-relaxed text-muted-foreground not-italic">
					<span>From</span>{" "}
					<span className="font-mono text-[0.8125rem] text-foreground">
						{message.from || "unknown"}
					</span>
					<span className="mx-1.5 text-border" aria-hidden>
						·
					</span>
					<time
						className="tabular-nums text-foreground/75"
						dateTime={message.received_at}
					>
						{new Date(message.received_at).toLocaleString()}
					</time>
				</p>
			</CardHeader>
			<CardContent className="space-y-4 px-5 py-5 text-sm">
				{message.text_body ? (
					<pre className="whitespace-pre-wrap rounded-lg border border-border/60 bg-muted/50 p-4 font-mono text-xs leading-relaxed text-foreground not-italic dark:bg-muted/35">
						{message.text_body}
					</pre>
				) : null}
				{message.html_body ? (
					<div
						className={cn(
							"prose prose-sm max-w-none rounded-lg border border-border/60 bg-muted/30 p-4 dark:prose-invert dark:bg-muted/25",
							"prose-headings:font-sans prose-headings:font-semibold prose-headings:not-italic prose-headings:text-foreground",
							"prose-p:text-foreground/90 prose-p:not-italic prose-li:not-italic",
							"[&_em]:not-italic [&_em]:font-normal [&_i]:not-italic",
							"prose-a:text-primary prose-strong:text-foreground",
						)}
						// biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized upstream; no attachments stored
						dangerouslySetInnerHTML={{ __html: message.html_body }}
					/>
				) : null}
			</CardContent>
		</Card>
	);
}
